import { useState } from 'react';
import { ThumbsUp, ThumbsDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { feedbackApi } from '@/lib/feedback';

/**
 * 👍/👎 relevance feedback on an Ask answer or a Search result. Optimistic;
 * a down-vote reveals an optional "what was off?" note. Feeds the §12 Phase-A
 * relevance trend (Reports) and the golden-set the eval harness consumes.
 */
export function FeedbackButtons({
  wsId,
  kind,
  targetId,
  query,
  citedChunkIds,
}: {
  wsId: string;
  kind: 'ask' | 'search';
  targetId: string;
  query?: string;
  citedChunkIds?: (string | number)[];
}) {
  const { t } = useTranslation();
  const [vote, setVote] = useState<1 | -1 | null>(null);
  const [note, setNote] = useState('');
  const [noteSent, setNoteSent] = useState(false);

  const cast = async (v: 1 | -1) => {
    setVote(v);
    try {
      await feedbackApi.vote(wsId, { kind, targetId, vote: v, query, citedChunkIds });
    } catch {
      /* best-effort — feedback is non-blocking */
    }
  };

  const sendNote = async () => {
    if (!note.trim()) return;
    try {
      await feedbackApi.vote(wsId, { kind, targetId, vote: -1, query, citedChunkIds, note: note.trim() });
      setNoteSent(true);
    } catch {
      /* ignore */
    }
  };

  return (
    <span className="inline-flex flex-col gap-1">
      <span className="inline-flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => cast(1)}
          title={t('feedback.helpful', { defaultValue: 'Helpful' }) as string}
          aria-pressed={vote === 1}
          className={`p-1 rounded transition ${vote === 1 ? 'text-emerald-600' : 'text-ink-400 hover:text-ink-700'}`}
        >
          <ThumbsUp size={14} />
        </button>
        <button
          type="button"
          onClick={() => cast(-1)}
          title={t('feedback.unhelpful', { defaultValue: 'Not helpful' }) as string}
          aria-pressed={vote === -1}
          className={`p-1 rounded transition ${vote === -1 ? 'text-rose-600' : 'text-ink-400 hover:text-ink-700'}`}
        >
          <ThumbsDown size={14} />
        </button>
        {vote !== null && (
          <span className="text-[11px] text-ink-400">{t('feedback.thanks', { defaultValue: 'Thanks' })}</span>
        )}
      </span>
      {vote === -1 && !noteSent && (
        <span className="inline-flex items-center gap-1.5">
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void sendNote(); } }}
            placeholder={t('feedback.note_ph', { defaultValue: 'What was off? (optional)' }) as string}
            className="text-[11px] border border-ink-200 rounded px-2 py-0.5 w-52 focus:outline-none focus:ring-1 focus:ring-accent-500/40"
          />
          <button type="button" onClick={() => void sendNote()} className="text-[11px] text-accent-600 hover:underline">
            {t('common.send', { defaultValue: 'Send' })}
          </button>
        </span>
      )}
    </span>
  );
}
